import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getParticipantColor } from "../../../utils/participantColors";
import { socketService } from "../../../services/socket";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatTimecode } from "../../../hooks/useTimecode";
import { parseSourceTimestampMs } from "../utils/sourceTime";

interface LiveTranscriptionsPanelProps {
  onSelectText: (text: string, tc: string, speaker: string) => void;
  baseTc: string;
  participants: any[];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  speakerAssignments?: {
    speakerId: string;
    name: string;
    assignedAt: number;
  }[];
  onSpeakerMap?: (sourceSpeaker: string, targetSpeaker: string, timestamp?: number) => void;
  onQuickLog?: (
    text: string,
    tc: string,
    speaker: string,
    isOtf?: boolean,
    sourceTimestamp?: number,
    startOffset?: number | null,
    endOffset?: number | null,
    channel?: number,
  ) => boolean | void;
  isAiActive?: boolean;
  onStartAi?: (channel: number) => void;
  onStopAi?: (channel: number) => void;
  /** Shared with ConsoleColumn when this panel is rendered inside Logger. */
  isOtfMode?: boolean;
  onOtfModeChange?: (enabled: boolean) => void;
  onTranscriptDragChange?: (timecodes: string[] | null) => void;
}

export const LiveTranscriptionsPanel: React.FC<LiveTranscriptionsPanelProps> = ({
  onSelectText,
  baseTc,
  participants,
  isCollapsed,
  onToggleCollapse,
  speakerAssignments = [],
  onSpeakerMap,
  onQuickLog,
  isAiActive: _isAiActive,
  onStartAi: _onStartAi,
  onStopAi: _onStopAi,
  isOtfMode: controlledOtfMode,
  onOtfModeChange,
  onTranscriptDragChange,
}) => {
  const { t: translate } = useTranslation();
  const [transcripts, setTranscripts] = useState<any[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [_currentIndex, _setCurrentIndex] = useState(0);
  const [activeChannel, setActiveChannel] = useLocalStorage<number>("logger_activeChannel", 1);
  const [localIsOtfMode, setLocalIsOtfMode] = useState<boolean>(false);
  const isOtfMode = controlledOtfMode ?? localIsOtfMode;
  const toggleOtfMode = () => {
    const next = !isOtfMode;
    if (controlledOtfMode === undefined) setLocalIsOtfMode(next);
    onOtfModeChange?.(next);
  };

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  // Formatter component to use external speaker mapping if any
  const [localSpeakerAssignments, setLocalSpeakerAssignments] = useState<
    { speakerId: string; name: string; assignedAt: number }[]
  >([]);

  // Combine both arrays: use external if available and populated, else local
  const activeAssignments =
    speakerAssignments.length > 0 ? speakerAssignments : localSpeakerAssignments;

  // Drag and Drop State
  const [_draggedSpeaker, setDraggedSpeaker] = useState<string | null>(null);

  const [autoScroll, setAutoScroll] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshStream = () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    socketService.reconnect();
    socketService.emit("rejoin-current-project");
    setTimeout(() => setIsRefreshing(false), 1200);
  };

  const baseTcRef = useRef(baseTc);
  useEffect(() => {
    baseTcRef.current = baseTc;
  }, [baseTc]);

  // Clear selection when changing channel
  useEffect(() => {
    setSelectedIds(new Set());
    setLastSelectedIndex(null);
  }, [activeChannel]);

  // Listen to real live transcriptions + watchdog for stale feeds
  useEffect(() => {
    // Watchdog: tracks the last time we received a transcription.
    // If silence exceeds the threshold while we're connected, force a room re-join.
    const TRANSCRIPTION_WATCHDOG_MS = 20_000; // 20s silence threshold
    // Seed at mount so the watchdog also covers the cold-start case where the
    // socket lost its room *before* the panel mounted (otherwise we'd wait
    // forever for a first message that never arrives).
    let lastTranscriptionAt = Date.now();

    const handleTranscription = (data: any) => {
      const receivedAt = Date.now();
      const parsedSourceAt = parseSourceTimestampMs(data.timestamp);
      const sourceAt = parsedSourceAt ?? receivedAt;
      lastTranscriptionAt = receivedAt;

      setTranscripts((curr) => {
        // Prefer the explicit tc from the payload. Otherwise derive it from
        // the server-provided timestamp (real transcript time). Only fall
        // back to baseTc / now() if both are missing.
        let resolvedTc: string;
        if (data.tc) {
          resolvedTc = data.tc;
        } else if (parsedSourceAt !== null) {
          resolvedTc = formatTimecode(new Date(parsedSourceAt));
        } else {
          const base =
            baseTcRef.current && baseTcRef.current.includes(":")
              ? baseTcRef.current
              : formatTimecode(new Date());
          resolvedTc = base;
        }

        const newTranscript = {
          id: data.id || Date.now().toString(),
          speaker: data.speaker || translate("logger.transcriptions.unknown"),
          text: data.text,
          tc: resolvedTc,
          isFinal: data.isFinal,
          // Keep transport receipt time separate from the content clock. The
          // former drives UI/speaker assignment; the latter becomes log.start_at.
          receivedAt,
          sourceAt,
          channel: data.channel || 1,
          // Word-level timestamps from STT provider (for OUT/DUR and future text-based editing)
          startOffset: data.startOffset ?? null,
          endOffset: data.endOffset ?? null,
          words: data.words || [],
        };

        // If we have a partial from the same speaker and same channel, replace it.
        // Otherwise, append it.
        const lastIdx = curr
          .map((t) => `${t.speaker}-${t.channel}`)
          .lastIndexOf(`${newTranscript.speaker}-${newTranscript.channel}`);

        // Deduplication: prevent identical text from appearing as a duplicate line
        // if it arrives consecutively (common artifact of socket reconnects or backend replays).
        if (lastIdx !== -1) {
          const last = curr[lastIdx];
          if (last.text === newTranscript.text && last.isFinal === newTranscript.isFinal) {
            // Exactly identical to the last one for this speaker, ignore duplicate
            return curr;
          }
          // If the last one was a partial, replace it with the new one (whether partial or final)
          if (!last.isFinal) {
            const next = [...curr];
            next[lastIdx] = newTranscript;
            // Optionally, if we wanted to move it to the end so chronological order is strictly maintained
            // when multiple speakers overlap, we could delete it and push to the end. But in-place is fine
            // for simple replacement.
            return next;
          }
        }

        // Append the new transcript
        const nextState = [...curr, newTranscript];

        const historyLimit = parseInt(
          import.meta.env.VITE_TRANSCRIPTION_HISTORY_LIMIT || "5000",
          10,
        );
        const targetKeep = Math.max(100, Math.floor(historyLimit * 0.8)); // Keep 80% of limit

        // Deduplicate or limit by full group boundaries to avoid chopping paragraphs
        // Instead of just .slice(-5000), we'll do a simple grouped cleanup
        if (nextState.length > historyLimit) {
          // Keep roughly the last N items, but shift to a boundary
          let cutIndex = nextState.length - targetKeep;

          // Try to find the start of a new speaker block near cutIndex
          // A boundary is where nextState[i].speaker !== nextState[i-1].speaker
          while (
            cutIndex > 0 &&
            cutIndex < nextState.length &&
            nextState[cutIndex].speaker === nextState[cutIndex - 1].speaker &&
            nextState[cutIndex].channel === nextState[cutIndex - 1].channel
          ) {
            cutIndex++; // Move forward to find the next boundary (drop the whole old group)
          }

          return nextState.slice(cutIndex);
        }

        return nextState;
      });
    };

    // Watchdog interval: fires when transcriptions stop arriving while we
    // believe the socket is connected. Two failure modes are covered:
    //   1) Lost room membership but socket transport is healthy → ack arrives,
    //      we rejoin via "rejoin-current-project".
    //   2) Zombie socket: transport is dead but `socket.connected` still
    //      reports true → the ack never fires. We arm a 3s timeout and force
    //      a hard reconnect so socket.io creates a fresh transport.
    const watchdogInterval = setInterval(() => {
      if (!socketService.connected) return;
      const silenceMs = Date.now() - lastTranscriptionAt;
      if (silenceMs > TRANSCRIPTION_WATCHDOG_MS) {
        console.warn(
          `⚠️ [Transcription Watchdog] ${Math.round(silenceMs / 1000)}s of silence — verifying room membership`,
        );

        let ackReceived = false;
        const zombieTimer = setTimeout(() => {
          if (ackReceived) return;
          console.warn(
            "⚠️ [Transcription Watchdog] No ack within 3s — socket likely zombie, forcing hard reconnect",
          );
          socketService.reconnect();
        }, 3000);

        socketService.emit("verify-room-membership", null, (inRoom: boolean) => {
          ackReceived = true;
          clearTimeout(zombieTimer);
          if (!inRoom) {
            console.warn("⚠️ [Transcription Watchdog] Confirmed NOT in room — re-joining");
            socketService.emit("rejoin-current-project");
          }
        });
        // Reset to prevent spamming — give it another full cycle to recover
        lastTranscriptionAt = Date.now();
      }
    }, TRANSCRIPTION_WATCHDOG_MS);

    socketService.on("live-transcription-feed", handleTranscription);
    return () => {
      socketService.off("live-transcription-feed", handleTranscription);
      clearInterval(watchdogInterval);
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [transcripts, autoScroll]);

  const handleScroll = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      // Allow a small threshold (e.g., 50px) to consider it "at the bottom"
      const isAtBottom =
        Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) < 50;
      setAutoScroll(isAtBottom);
    }
  };

  const resolveSpeakerName = (speakerId: string, timestamp: number) => {
    // Find all assignments for this speaker, preserving insertion order
    // (most recent user intent at the end of the array).
    const assignments = activeAssignments.filter((a) => a.speakerId === speakerId);
    if (assignments.length === 0) return speakerId;

    // El assignedAt es el receivedAt del grupo sobre el que se soltó.
    // Aplica a esa transcripción y a todas las que estén por debajo
    // (receivedAt >= assignedAt). Recorremos del más reciente al más
    // viejo para que un drop posterior siempre gane sobre uno anterior,
    // aunque su assignedAt apunte a un grupo más viejo.
    for (let i = assignments.length - 1; i >= 0; i--) {
      if (assignments[i].assignedAt <= timestamp) return assignments[i].name;
    }

    return speakerId;
  };

  const visibleTranscripts = transcripts.filter((t) => t.channel === activeChannel);

  const groupedTranscripts = React.useMemo(() => {
    const groups: any[] = [];
    let currentGroup: any = null;

    for (const t of visibleTranscripts) {
      const mappedSpeaker = resolveSpeakerName(t.speaker, t.receivedAt);

      let shouldCreateNewGroup = false;

      if (!currentGroup || currentGroup.mappedSpeaker !== mappedSpeaker) {
        shouldCreateNewGroup = true;
      } else {
        // Check if the last item ended with a sentence-ending punctuation
        const lastItemText = currentGroup.items[currentGroup.items.length - 1].text.trim();
        const endsWithPunctuation = /[.!?]$/.test(lastItemText);

        // Si acabamos de tener un punto de separación, separamos en un nuevo párrafo
        if (endsWithPunctuation) {
          shouldCreateNewGroup = true;
        }
      }

      if (shouldCreateNewGroup) {
        if (currentGroup) groups.push(currentGroup);
        currentGroup = {
          id: t.id,
          tc: t.tc,
          mappedSpeaker,
          speakerId: t.speaker,
          receivedAt: t.receivedAt,
          sourceAt: t.sourceAt,
          channel: t.channel,
          isFinal: t.isFinal,
          items: [t],
        };
      } else {
        currentGroup.items.push(t);
        currentGroup.isFinal = t.isFinal;
      }
    }
    if (currentGroup) groups.push(currentGroup);
    return groups;
  }, [visibleTranscripts, activeAssignments]);

  const handleItemClick = (e: React.MouseEvent, groupId: string, index: number) => {
    if (e.shiftKey && lastSelectedIndex !== null) {
      // Rango de selección
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);

      const newSelection = new Set(selectedIds);
      for (let i = start; i <= end; i++) {
        newSelection.add(groupedTranscripts[i].id);
      }
      setSelectedIds(newSelection);
    } else if (e.metaKey || e.ctrlKey) {
      // Añadir/quitar toggle
      const newSelection = new Set(selectedIds);
      if (newSelection.has(groupId)) {
        newSelection.delete(groupId);
      } else {
        newSelection.add(groupId);
      }
      setSelectedIds(newSelection);
      setLastSelectedIndex(index);
    } else {
      // Selección única
      setSelectedIds(new Set([groupId]));
      setLastSelectedIndex(index);
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim() !== "") {
      const selectedText = selection.toString().trim();
      // Need to locate data attributes on the paragraph
      let parent = selection.anchorNode?.parentElement?.closest(".transcript-item");
      if (!parent && selection.anchorNode?.parentElement?.classList.contains("transcript-item")) {
        parent = selection.anchorNode.parentElement;
      }
      if (parent) {
        const tc = parent.getAttribute("data-tc") || baseTc;
        const speaker =
          parent.getAttribute("data-speaker") || translate("logger.transcriptions.unknown");
        onSelectText(selectedText, tc, speaker);
      }
      // Clear selection after capture to give visual feedback
      selection.removeAllRanges();
    }
  };

  if (isCollapsed) {
    return (
      <div
        data-testid="live-transcriptions-collapsed"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: "var(--color-bg-mesh)",
          border: "1px solid var(--color-panel-border)",
          borderRadius: "8px",
          overflow: "hidden",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          transition: "all 0.3s ease",
        }}
        onClick={onToggleCollapse}
        title={translate("logger.transcriptions.expandTranscriptions")}
      >
        <div
          style={{
            transform: "rotate(-90deg)",
            whiteSpace: "nowrap",
            color: "#fff",
            fontWeight: 900,
            letterSpacing: "1px",
            fontSize: "var(--text-sm)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: "var(--color-accent-danger)",
              boxShadow: "0 0 8px var(--color-accent-danger)",
            }}
          />
          {translate("logger.transcriptions.showTranscriptions")}{" "}
          <i className="bx bx-expand-horizontal" style={{ marginLeft: "5px" }}></i>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="live-transcriptions-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "var(--color-bg-mesh)",
        border: "1px solid var(--color-panel-border)",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        data-testid="live-transcriptions-header"
        className="flex flex-col gap-1.5 border-b border-[var(--color-panel-border)] bg-white/[0.03] px-3 py-2"
      >
        {/* Row 1: Title */}
        <div className="flex items-center gap-2">
          <div className="size-2 rounded-full bg-[var(--color-accent-danger)] shadow-[0_0_8px_var(--color-accent-danger)]" />
          <span
            className="font-black tracking-[1px] text-white uppercase"
            style={{ fontSize: "var(--text-sm)" }}
          >
            {translate("logger.transcriptions.liveTranscriptions")}{" "}
            <span className="opacity-50" style={{ fontSize: "var(--text-2xs)" }}>
              {translate("logger.transcriptions.beta")}
            </span>
          </span>
        </div>

        {/* Row 2: Stream selector + controls */}
        <div className="flex items-center gap-2">
          {/* Channel Selector */}
          <div className="flex gap-1">
            {[1, 2].map((ch) => (
              <Badge
                key={ch}
                variant={activeChannel === ch ? "default" : "outline"}
                size="xs"
                className={`cursor-pointer select-none ${activeChannel === ch ? "bg-[var(--color-accent-danger)] text-white border-transparent hover:bg-[var(--color-accent-danger)]/90" : "text-muted-foreground hover:text-foreground"}`}
                onClick={() => setActiveChannel(ch)}
              >
                {ch === 1
                  ? translate("logger.transcriptions.stream1")
                  : translate("logger.transcriptions.stream2")}
              </Badge>
            ))}
          </div>

          {/* OTF Switch */}
          <Badge
            variant={isOtfMode ? "default" : "outline"}
            size="xs"
            className={`cursor-pointer select-none ml-2 ${isOtfMode ? "bg-[#00e5ff] text-black border-transparent hover:bg-[#00e5ff]/90" : "text-muted-foreground hover:text-foreground"}`}
            onClick={toggleOtfMode}
            title="Activar para registrar las transcripciones como entrevistas (OTF)"
          >
            <i className={`bx ${isOtfMode ? "bx-microphone" : "bx-microphone-off"} mr-1`}></i>
            OTF MODE
          </Badge>

          {/* Refresh stream button */}
          <button
            type="button"
            onClick={handleRefreshStream}
            disabled={isRefreshing}
            title="Refrescar el stream en vivo (reconecta sin recargar la app)"
            className="ml-auto inline-flex items-center justify-center size-6 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="refresh-live-stream"
          >
            <i
              className="bx bx-refresh"
              style={{
                fontSize: "16px",
                display: "inline-block",
                animation: isRefreshing ? "spin 0.8s linear infinite" : undefined,
              }}
            ></i>
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        onMouseUp={handleTextSelection}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "12px",
          display: isCollapsed ? "none" : "flex",
          flexDirection: "column",
          gap: "2px",
          position: "relative",
        }}
      >
        {transcripts.length === 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              opacity: 0.5,
              gap: "8px",
            }}
          >
            <i className="bx bx-microphone" style={{ fontSize: "2rem" }}></i>
            <span style={{ fontSize: "var(--text-base)" }}>
              {translate("logger.transcriptions.waitingForAudio")}
            </span>
            <span style={{ fontSize: "var(--text-sm)" }}>
              {translate("logger.transcriptions.checkRelay")}
            </span>
          </div>
        )}
        {groupedTranscripts.map((group, idx) => {
          const mappedSpeaker = group.mappedSpeaker;

          // Try to match color from participants array if mapped
          const pMatch = participants.find(
            (p) =>
              p.name?.toUpperCase() === mappedSpeaker ||
              p.alias?.toUpperCase() === mappedSpeaker ||
              p.code?.toUpperCase() === mappedSpeaker,
          );

          let badgeColor = "rgba(255,255,255,0.1)";
          let textColor = "#fff";
          if (pMatch) {
            const pColor = getParticipantColor(
              pMatch.metadata?.colorId,
              pMatch.metadata?.customColor,
            );
            badgeColor = pColor.bg.includes("gradient") ? pColor.bg : pColor.bg || badgeColor;
            textColor = pColor.text || textColor;
            if (badgeColor.includes("repeating-linear-gradient") || badgeColor === "transparent") {
              badgeColor = pColor.border || "rgba(255,255,255,0.1)";
            }
          }

          const getFullText = () => {
            return group.items
              .map((t: any) => t.text.trim())
              .filter(Boolean)
              .join(" ")
              .replace(/\s+([.,?!])/g, "$1");
          };

          return (
            <div
              key={group.id}
              data-testid={`transcript-item-${idx}`}
              className="transcript-item"
              data-tc={group.tc}
              data-speaker={mappedSpeaker}
              onClick={(e) => handleItemClick(e, group.id, idx)}
              onDoubleClick={(e) => {
                if (onQuickLog) {
                  // Get endOffset from the last item in the group
                  const lastItem = group.items[group.items.length - 1];
                  const groupEndOffset = lastItem?.endOffset ?? null;
                  onQuickLog(
                    getFullText(),
                    group.tc,
                    mappedSpeaker,
                    isOtfMode || e.shiftKey,
                    group.sourceAt,
                    group.items[0]?.startOffset ?? null,
                    groupEndOffset,
                    group.channel,
                  );
                }
              }}
              draggable={true}
              onDragStart={(e) => {
                // Si se arrastra un elemento no seleccionado de momento, arrastramos solo ese
                let idsToDrag = selectedIds.has(group.id) ? Array.from(selectedIds) : [group.id];

                // Filtrar y ordenar los items seleccionados para que queden en el orden correcto
                const itemsToDrag = groupedTranscripts.filter((g) => idsToDrag.includes(g.id));
                onTranscriptDragChange?.(itemsToDrag.map((item) => item.tc));

                const plainText = itemsToDrag
                  .map((g) => {
                    const fullText = g.items
                      .map((t: any) => t.text.trim())
                      .filter(Boolean)
                      .join(" ")
                      .replace(/\s+([.,?!])/g, "$1");
                    return `[${g.tc}] [${g.mappedSpeaker}] "${fullText}"`;
                  })
                  .join("\n");

                e.dataTransfer.setData("text/plain", plainText);
                e.dataTransfer.setData(
                  "application/json",
                  JSON.stringify({
                    text: plainText,
                    isOtf: isOtfMode,
                    sources: itemsToDrag.map((g) => g.sourceAt),
                    startAts: itemsToDrag.map((g) => {
                      const first = g.items[0];
                      return first?.startOffset;
                    }),
                    endAts: itemsToDrag.map((g) => {
                      const last = g.items[g.items.length - 1];
                      return last?.endOffset;
                    }),
                    words: itemsToDrag.map((g) => g.items.flatMap((i: any) => i.words || [])),
                    channels: itemsToDrag.map((g) => g.channel),
                  }),
                );
                e.dataTransfer.effectAllowed = "copy";

                // When dragging a single group, expose speaker metadata so that
                // dropping anywhere on the row (not just the small pill) onto a
                // participant still reassigns the speaker. Without this, drops
                // off the pill silently no-op and the user has to retry.
                if (itemsToDrag.length === 1) {
                  const only = itemsToDrag[0];
                  e.dataTransfer.setData("speaker-name", only.mappedSpeaker);
                  e.dataTransfer.setData("speaker-id", only.speakerId);
                  e.dataTransfer.setData("source-received-at", String(only.receivedAt));
                }

                // Rich drag ghost with content preview
                const dragGhost = document.createElement("div");
                const recentLogsPanel = document.querySelector<HTMLElement>(
                  '[data-testid="console-recent-logs"]',
                );
                const recentLogsWidth = recentLogsPanel?.getBoundingClientRect().width;
                const dragGhostWidth =
                  typeof recentLogsWidth === "number" && recentLogsWidth > 0
                    ? Math.max(280, recentLogsWidth - 16)
                    : 340;
                dragGhost.style.cssText = `
                  position: absolute; top: -1000px;
                  width: ${dragGhostWidth}px; max-width: calc(100vw - 32px); padding: 8px 12px;
                  box-sizing: border-box; border-radius: 4px; font-size: 11px;
                  font-family: system-ui, sans-serif;
                  box-shadow: 0 8px 24px rgba(0,0,0,0.5);
                  display: flex; flex-direction: column; gap: 4px;
                  border-left: 3px solid ${isOtfMode ? "#00e5ff" : "#9c27b0"};
                  background: ${isOtfMode ? "rgba(20, 50, 54, 0.68)" : "rgba(46, 31, 49, 0.68)"};
                  opacity: 0.58;
                  color: rgba(255, 255, 255, 0.9);
                `;

                // Header row: OTF badge + speaker + count
                const headerRow = document.createElement("div");
                headerRow.style.cssText =
                  "display: flex; align-items: center; gap: 6px; font-weight: 700;";

                if (isOtfMode) {
                  const otfBadge = document.createElement("span");
                  otfBadge.style.cssText =
                    "background: #00e5ff; color: #000; padding: 1px 6px; border-radius: 4px; font-size: 9px; font-weight: 900; letter-spacing: 0.5px;";
                  otfBadge.textContent = "OTF";
                  headerRow.appendChild(otfBadge);
                }

                if (itemsToDrag.length > 1) {
                  const countBadge = document.createElement("span");
                  countBadge.style.cssText =
                    "background: rgba(255,255,255,0.15); padding: 1px 6px; border-radius: 4px; font-size: 9px;";
                  countBadge.textContent = `${itemsToDrag.length} bloques`;
                  headerRow.appendChild(countBadge);
                }

                const speakerSpan = document.createElement("span");
                speakerSpan.style.cssText = "font-size: 10px; opacity: 0.7;";
                speakerSpan.textContent =
                  itemsToDrag.length === 1
                    ? group.mappedSpeaker
                    : `${itemsToDrag.length} transcripciones`;
                headerRow.appendChild(speakerSpan);
                dragGhost.appendChild(headerRow);

                // Text preview (first item, truncated)
                const preview = document.createElement("div");
                preview.style.cssText =
                  "font-size: 11px; color: rgba(255,255,255,0.7); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%;";
                const firstText = itemsToDrag[0].items
                  .map((t: any) => t.text.trim())
                  .filter(Boolean)
                  .join(" ");
                preview.textContent =
                  firstText.length > 60 ? firstText.substring(0, 60) + "…" : firstText;
                dragGhost.appendChild(preview);

                // TC row
                const tcRow = document.createElement("div");
                tcRow.style.cssText = "font-family: monospace; font-size: 10px; opacity: 0.5;";
                tcRow.textContent = `TC: ${itemsToDrag[0].tc}`;
                dragGhost.appendChild(tcRow);

                document.body.appendChild(dragGhost);
                e.dataTransfer.setDragImage(dragGhost, 20, 20);

                setTimeout(() => {
                  if (document.body.contains(dragGhost)) {
                    document.body.removeChild(dragGhost);
                  }
                }, 0);
              }}
              onDragEnd={(e) => {
                onTranscriptDragChange?.(null);
                // Reset selection after a successful drop so a follow-up
                // shift-click doesn't silently re-include items the user
                // already moved — that was the source of duplicate logs.
                // Cancelled drags ("none") keep the selection intact.
                if (e.dataTransfer.dropEffect !== "none") {
                  setSelectedIds(new Set());
                  setLastSelectedIndex(null);
                }
              }}
              style={{
                fontSize: "var(--text-base)",
                lineHeight: "1.3",
                color: "#ccc",
                transition: "all 0.15s ease",
                padding: "2px 8px",
                margin: "0",
                borderRadius: "6px",
                opacity: group.isFinal ? 1 : 0.6,
                background: selectedIds.has(group.id) ? "rgba(255, 255, 255, 0.1)" : "transparent",
                border: selectedIds.has(group.id)
                  ? "1px solid rgba(255, 255, 255, 0.2)"
                  : "1px solid transparent",
                cursor: "grab",
                userSelect: "auto",
              }}
            >
              <span
                style={{
                  color: "#666",
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--text-sm)",
                  marginRight: "8px",
                }}
              >
                [{group.tc}]
              </span>
              <span
                draggable={!selectedIds.has(group.id)}
                onDragStart={(e) => {
                  if (selectedIds.has(group.id)) {
                    e.preventDefault();
                    return;
                  }
                  e.stopPropagation(); // IMPORTANTE: No arrastrar todo el bloque si se coge la píldora de speaker
                  setDraggedSpeaker(mappedSpeaker);

                  const fullText = group.items
                    .map((t: any) => t.text.trim())
                    .filter(Boolean)
                    .join(" ")
                    .replace(/\s+([.,?!])/g, "$1");
                  const plainText = `[${group.tc}] [${mappedSpeaker}] "${fullText}"`;

                  e.dataTransfer.effectAllowed = "copyMove";
                  e.dataTransfer.setData("text/plain", plainText);
                  e.dataTransfer.setData(
                    "application/json",
                    JSON.stringify({
                      text: plainText,
                      isOtf: isOtfMode,
                      sources: [group.sourceAt],
                      startAts: [group.items[0]?.startOffset],
                      endAts: [group.items[group.items.length - 1]?.endOffset],
                      words: [group.items.flatMap((i: any) => i.words || [])],
                      channels: [group.channel],
                    }),
                  );
                  e.dataTransfer.setData("speaker-name", mappedSpeaker);
                  e.dataTransfer.setData("speaker-id", group.speakerId); // The original speaker ID
                  // Set data as string but explicitly
                  e.dataTransfer.setData("source-received-at", String(group.receivedAt));

                  // Rich drag ghost for speaker badge drag
                  const dragGhost = document.createElement("div");
                  dragGhost.style.cssText = `
                    position: absolute; top: -1000px;
                    max-width: 300px; padding: 6px 10px;
                    border-radius: 8px; font-size: 11px;
                    font-family: system-ui, sans-serif;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.5);
                    display: flex; flex-direction: column; gap: 3px;
                    border-left: 3px solid ${isOtfMode ? "#00e5ff" : badgeColor};
                    background: rgba(0, 0, 0, 0.85);
                    backdrop-filter: blur(8px);
                    color: rgba(255, 255, 255, 0.9);
                  `;

                  // Header: OTF badge + speaker name
                  const headerRow = document.createElement("div");
                  headerRow.style.cssText =
                    "display: flex; align-items: center; gap: 6px; font-weight: 700;";

                  if (isOtfMode) {
                    const otfBadge = document.createElement("span");
                    otfBadge.style.cssText =
                      "background: #00e5ff; color: #000; padding: 1px 6px; border-radius: 4px; font-size: 9px; font-weight: 900; letter-spacing: 0.5px;";
                    otfBadge.textContent = "OTF";
                    headerRow.appendChild(otfBadge);
                  }

                  const speakerBadge = document.createElement("span");
                  speakerBadge.style.cssText = `background: ${badgeColor}; color: ${textColor}; padding: 1px 8px; border-radius: 4px; font-size: 10px; font-weight: 700;`;
                  speakerBadge.textContent = mappedSpeaker;
                  headerRow.appendChild(speakerBadge);
                  dragGhost.appendChild(headerRow);

                  // Text preview
                  const preview = document.createElement("div");
                  preview.style.cssText =
                    "font-size: 10px; color: rgba(255,255,255,0.6); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 275px;";
                  const previewText =
                    fullText.length > 55 ? fullText.substring(0, 55) + "…" : fullText;
                  preview.textContent = previewText;
                  dragGhost.appendChild(preview);

                  document.body.appendChild(dragGhost);
                  e.dataTransfer.setDragImage(dragGhost, 20, 16);

                  setTimeout(() => {
                    if (document.body.contains(dragGhost)) {
                      document.body.removeChild(dragGhost);
                    }
                  }, 0);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "copy";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const sourceSpeakerId = e.dataTransfer.getData("speaker-id");
                  const sourceSpeakerName = e.dataTransfer.getData("speaker-name");
                  const plainText = e.dataTransfer.getData("text/plain");
                  const mappedText = sourceSpeakerName || plainText;

                  // El mapeo aplica desde el grupo destino hacia abajo,
                  // incluyendo a la propia transcripción sobre la que se soltó.
                  const assignedAt = Number(group.receivedAt);

                  if (sourceSpeakerId) {
                    if (sourceSpeakerId !== group.speakerId) {
                      if (onSpeakerMap) {
                        onSpeakerMap(group.speakerId, mappedText.toUpperCase(), assignedAt);
                      } else {
                        setLocalSpeakerAssignments((prev) => [
                          ...prev,
                          {
                            speakerId: group.speakerId,
                            name: mappedText.toUpperCase(),
                            assignedAt,
                          },
                        ]);
                      }
                    }
                  } else if (plainText) {
                    if (onSpeakerMap) {
                      onSpeakerMap(group.speakerId, plainText.toUpperCase(), assignedAt);
                    } else {
                      setLocalSpeakerAssignments((prev) => [
                        ...prev,
                        {
                          speakerId: group.speakerId,
                          name: plainText.toUpperCase(),
                          assignedAt,
                        },
                      ]);
                    }
                  }
                }}
                className="inline-flex items-center mr-2 cursor-grab select-none"
              >
                <Badge
                  variant="outline"
                  size="xs"
                  className="border-0"
                  style={{
                    background: badgeColor,
                    color: textColor,
                    boxShadow:
                      badgeColor !== "rgba(255,255,255,0.1)" ? "0 1px 3px rgba(0,0,0,0.5)" : "none",
                  }}
                >
                  {mappedSpeaker}
                </Badge>
              </span>
              <span style={{ cursor: "text" }}>
                {group.items.map((t: any, i: number) => {
                  const trimmed = t.text.trim();
                  if (!trimmed) return null;
                  const isPunctuation = /^[.,?!]/.test(trimmed);
                  const space = i > 0 && !isPunctuation ? " " : "";
                  return (
                    <React.Fragment key={t.id}>
                      {space}
                      {trimmed}
                    </React.Fragment>
                  );
                })}
              </span>
            </div>
          );
        })}
      </div>

      <style>{`
                .transcript-item ::selection {
                    background: var(--color-accent-danger) !important;
                    color: white !important;
                    text-shadow: 0 0 5px rgba(255,255,255,0.5) !important;
                }
                .transcript-item span[draggable]:active {
                    cursor: grabbing !important;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
    </div>
  );
};
